import {
  authenticatedFetch,
  resolvePeriverseApiBaseUrl,
} from '@peritasai/data-access';
import {
  AssetEntity,
  assetRepository,
  caseScheduleRepository,
  ensureCachedLocations,
  LocationEntity,
  locationRepository,
  SurgicalCaseEntity,
  useAllLocations,
  useAndroid,
  useLocations,
} from '@peritasai/repository';
import { AssetLifecycleFunction, AssignTrackedItemsBatchDto, LocationTypeName } from '@peritasai/types';
import type { AssetFilterParams } from '@peritasai/types';
import { useCallback } from 'react';
import { formatTimeLabel } from '../../../shared';
import type { AssignToCaseCase } from '../types/assign-to-case';
import { DISCOVERABLE_ASSET_TYPES } from '../types/assign-to-case';
import { z } from 'zod';

const toAssignToCaseCase = (entity: SurgicalCaseEntity): AssignToCaseCase => ({
  id: entity.caseHospitalId || entity.id,
  caseId: entity.id,
  timeLabel: formatTimeLabel(entity.dateTime),
  surgeonName: entity.surgeon,
  procedureName: entity.caseType,
  scheduledDateTime: entity.dateTime,
  durationMinutes: entity.duration > 0 ? entity.duration : null,
});

type ResetDiscoveryRequest = {
  hubId: string;
  /** Id generated by the frontend to track the request */
  scanId: string;
};

const PeritrackCommandResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
});
type PeritrackCommandResponse = z.infer<typeof PeritrackCommandResponseSchema>;

type AssignTrackedItemRequest = {
  caseId: string;
  assetId: string;
};

const AssignTrackedItemResponseSchema = z.object({
  id: z.string(),
  caseId: z.string().optional(),
});
type AssignTrackedItemResponse = z.infer<
  typeof AssignTrackedItemResponseSchema
>;

type AssignTrackedItemsBatchRequest = AssignTrackedItemsBatchDto & {
  caseId: string;
};

type ResetDiscoveryResponse = {
  ok: boolean;
  status: number;
  data?: PeritrackCommandResponse;
};

export interface AssignToCaseService {
  getLocations: () => Promise<LocationEntity[]>;
  getCasesForLocation: (locationId: string) => Promise<AssignToCaseCase[]>;
  getAssetsForLocation: (locationId: string) => Promise<AssetEntity[]>;
  getAssetsForLocationAndChild: (locationId: string) => Promise<AssetEntity[]>;
  resetDiscovery: (
    payload: ResetDiscoveryRequest
  ) => Promise<ResetDiscoveryResponse>;
  assignTrackedItem: (payload: AssignTrackedItemRequest) => Promise<{
    ok: boolean;
    status: number;
    data?: AssignTrackedItemResponse;
  }>;
  assignTrackedItemsBatch: (
    payload: AssignTrackedItemsBatchRequest
  ) => Promise<{ ok: boolean }>;
}

export function useAssignToCaseService(): AssignToCaseService {
  const { isAndroid, resetDiscovery: androidResetDiscovery } = useAndroid();
  const locationQuery = useAllLocations(); //Caches data

  const getLocations = useCallback(async (): Promise<LocationEntity[]> => {
    const lifecycle = AssetLifecycleFunction.Surgery;
    const searchLocationType = LocationTypeName.OPERATING_ROOM;
    const locationData = await ensureCachedLocations(locationQuery);

    return locationData
      .filter((l) => l.assetLifecycleFunction === lifecycle &&
          l.locationType === searchLocationType)
      .sort((a, b) =>
        a.name.localeCompare(b.name, undefined, {
          numeric: true,
          sensitivity: 'base',
        })
      );
  }, [locationQuery]);

  const getCasesForLocation = useCallback(
    async (locationId: string): Promise<AssignToCaseCase[]> => {
      const cases = await caseScheduleRepository.getTodayCasesForLocation(
        locationId
      );
      return cases.map(toAssignToCaseCase);
    },
    []
  );
  const getAssetsForLocation = useCallback(
    async (locationId: string): Promise<AssetEntity[]> => {
      const allFilters: AssetFilterParams = {
        type: DISCOVERABLE_ASSET_TYPES.join(','),
        locationId,
        pageSize: 100,
        includeAssignedCase: true
      };
      return assetRepository.getAssets(allFilters);
    },
    []
  );

  const getAssetsForLocationAndChild = useCallback(
    async (locationId: string): Promise<AssetEntity[]> => {
      //Walk Hierarchy
      const locationData = await ensureCachedLocations(locationQuery);
      const location = locationData.find((loc) => loc.id === locationId);
      if (location) {
        const results: AssetEntity[][] = await Promise.all(
          [location.id, ...location.children].map((locationId) =>
            getAssetsForLocation(locationId)
          )
        );

        return results.flat();
      }
      return [];
    },
    []
  );

  const resetDiscovery = useCallback(
    async (payload: ResetDiscoveryRequest): Promise<ResetDiscoveryResponse> => {
      if (isAndroid) {
        androidResetDiscovery();
        return { ok: true, status: 200 };
      } else {
        const baseUrl = resolvePeriverseApiBaseUrl();
        const response = await authenticatedFetch(
          `${baseUrl}/peritrack/control/reset-discovery`,
          {
            method: 'POST',
            headers: {
              accept: 'application/json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
          }
        );
        const raw = await response.json().catch(() => null);
        const parsed = PeritrackCommandResponseSchema.safeParse(raw);
        const data: PeritrackCommandResponse | undefined = parsed.success
          ? parsed.data
          : undefined;
        return { ok: response.ok, status: response.status, data };
      }
    },
    [androidResetDiscovery, isAndroid]
  );

  const assignTrackedItem = useCallback(
    async (
      payload: AssignTrackedItemRequest
    ): Promise<{
      ok: boolean;
      status: number;
      data?: AssignTrackedItemResponse;
    }> => {
      const baseUrl = resolvePeriverseApiBaseUrl();
      const response = await authenticatedFetch(
        `${baseUrl}/cases/${encodeURIComponent(
          payload.caseId
        )}/tracked-items/assign`,
        {
          method: 'POST',
          headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ assetId: payload.assetId }),
        }
      );
      const raw = await response.json().catch(() => null);
      const parsed = AssignTrackedItemResponseSchema.safeParse(raw);
      const data: AssignTrackedItemResponse | undefined = parsed.success
        ? parsed.data
        : undefined;
      return { ok: response.ok, status: response.status, data };
    },
    []
  );
  const assignTrackedItemsBatch = useCallback(
    async (payload: AssignTrackedItemsBatchRequest): Promise<{ ok: boolean }> => {
      try {
        await caseScheduleRepository.assignTrackedItemsBatch(
          payload.caseId,
          payload.assetIds,
          payload.firstCartAssetId
        );
        return { ok: true };
      } catch (error) {
        console.error('Failed to assign assets to case', error);
        return { ok: false };
      }
    },
    []
  );

  return {
    getLocations,
    getCasesForLocation,
    getAssetsForLocation,
    getAssetsForLocationAndChild,
    resetDiscovery,
    assignTrackedItem,
    assignTrackedItemsBatch,
  };
}
